export type Language = 'uk' | 'en';

export interface Translations {
  appName: string;
  slogan: string;
  steps: {
    city: string;
    warehouse: string;
    contactInfo: string;
  };
  cityStep: {
    title: string;
    infoText: string;
    info: string;
    placeholder: string;
    noResults: string;
    typeToSearch: string;
    loading: string;
    popularCities: string;
  };
  warehouseStep: {
    title: string;
    selectedCity: string;
    placeholder: string;
    noResults: string;
    noWarehouses: string;
    loading: string;
  };
  contactStep: {
    title: string;
    selectedCity: string;
    selectedWarehouse: string;
    fullName: {
      label: string;
      placeholder: string;
    };
    phone: {
      label: string;
      placeholder: string;
    };
  };
  buttons: {
    next: string;
    back: string;
    submit: string;
  };
  success: {
    title: string;
    message: string;
    redirecting: string;
  };
  payment: {
    amountInfo: string;
    amountWithItemInfo: string;
    redirectInfo: string;
    redirectWithItemInfo: string;
    payWithMonobank: string;
    copyCardNumber: string;
    cardNumberCopied: string;
  };
  urlCreator: {
    title: string;
    itemName: {
      label: string;
      placeholder: string;
    };
    price: {
      label: string;
      placeholder: string;
    };
    createButton: string;
    copiedNotification: string;
  };
  navigation: {
    form: string;
    urlCreator: string;
  };
  footer: {
    copyright: string;
  };
}

export const translations: Record<Language, Translations> = {
  uk: {
    appName: 'Sale It To Me Baby',
    slogan: 'Вибір складу',
    steps: {
      city: 'Місто',
      warehouse: 'Відділення',
      contactInfo: 'Контактна інформація',
    },
    cityStep: {
      title: 'Виберіть ваше місто',
      infoText: 'Почніть вводити назву міста. Виберіть зі списку.',
      info: 'Почніть вводити назву міста. Виберіть зі списку.',
      placeholder: 'Почніть вводити назву міста',
      noResults: 'Міст не знайдено',
      typeToSearch: 'Введіть текст для пошуку міст',
      loading: 'Завантаження міст...',
      popularCities: 'Найпопулярніші міста',
    },
    warehouseStep: {
      title: 'Виберіть відділення',
      selectedCity: 'Вибране місто:',
      placeholder: 'Пошук відділення/поштомату за номером',
      noResults: 'Відділень не знайдено',
      noWarehouses: 'Немає доступних відділень для цього міста',
      loading: 'Завантаження відділень...',
    },
    contactStep: {
      title: 'Контактна інформація',
      selectedCity: 'Вибране місто:',
      selectedWarehouse: 'Вибране відділення:',
      fullName: {
        label: 'ПІБ',
        placeholder: 'ПІБ',
      },
      phone: {
        label: 'Номер телефону',
        placeholder: '+380XXXXXXXXX',
      },
    },
    buttons: {
      next: 'Далі',
      back: 'Назад',
      submit: 'Відправити замовлення',
    },
    success: {
      title: '👍',
      message: 'Інформація збережена, дякую. Виберіть зручний метод оплати нижче.',
      redirecting: 'Перенаправлення на сторінку оплати...',
    },
    payment: {
      amountInfo: '{amount} грн',
      amountWithItemInfo: 'Сума до оплати: {amount} грн за {item}',
      redirectInfo: 'Перенаправлення на сторінку оплати Monobank. Сума: {amount} грн',
      redirectWithItemInfo: 'Перенаправлення на сторінку оплати Monobank. Сума: {amount} грн за {item}',
      payWithMonobank: 'Оплатити з Monobank',
      copyCardNumber: 'Скопіювати реквізити для оплати',
      cardNumberCopied: 'Номер картки скопійовано!',
    },
    urlCreator: {
      title: 'Створення URL',
      itemName: {
        label: 'Назва товару',
        placeholder: 'Введіть назву товару',
      },
      price: {
        label: 'Ціна (грн)',
        placeholder: 'Введіть ціну',
      },
      createButton: 'Створити URL',
      copiedNotification: 'URL скопійовано в буфер обміну!',
    },
    navigation: {
      form: 'Форма',
      urlCreator: 'Створення URL',
    },
    footer: {
      copyright: '© {year} Sale It To Me Baby',
    },
  },
  en: {
    appName: 'Sale It To Me Baby',
    slogan: 'Warehouse Selection',
    steps: {
      city: 'City',
      warehouse: 'Warehouse',
      contactInfo: 'Contact Info',
    },
    cityStep: {
      title: 'Select Your City',
      infoText: 'Start by typing your city name. Select from the dropdown list.',
      info: 'Start by typing your city name. Select from the dropdown list.',
      placeholder: 'Search for city...',
      noResults: 'No cities found',
      typeToSearch: 'Type to search for cities',
      loading: 'Loading cities...',
      popularCities: 'Popular Cities',
    },
    warehouseStep: {
      title: 'Select Warehouse',
      selectedCity: 'Selected City:',
      placeholder: 'Search for warehouse by number or address...',
      noResults: 'No matching warehouses found',
      noWarehouses: 'No warehouses available for this city',
      loading: 'Loading warehouses...',
    },
    contactStep: {
      title: 'Contact Information',
      selectedCity: 'Selected City:',
      selectedWarehouse: 'Selected Warehouse:',
      fullName: {
        label: 'Full Name',
        placeholder: 'Enter your full name',
      },
      phone: {
        label: 'Phone Number',
        placeholder: 'Enter your phone number (e.g., +380XXXXXXXXX)',
      },
    },
    buttons: {
      next: 'Next',
      back: 'Back',
      submit: 'Submit Order',
    },
    success: {
      title: 'Success!',
      message: 'Your form has been submitted successfully.',
      redirecting: 'Redirecting to payment page...',
    },
    payment: {
      amountInfo: 'Payment amount: {amount} UAH',
      amountWithItemInfo: 'Payment amount: {amount} UAH for {item}',
      redirectInfo: 'Redirecting to Monobank payment page. Amount: {amount} UAH',
      redirectWithItemInfo: 'Redirecting to Monobank payment page. Amount: {amount} UAH for {item}',
      payWithMonobank: 'Pay with Monobank',
      copyCardNumber: 'Copy Card Number',
      cardNumberCopied: 'Card number copied!',
    },
    urlCreator: {
      title: 'URL Creator',
      itemName: {
        label: 'Item Name',
        placeholder: 'Enter item name',
      },
      price: {
        label: 'Price (UAH)',
        placeholder: 'Enter price',
      },
      createButton: 'Create URL',
      copiedNotification: 'URL copied to clipboard!',
    },
    navigation: {
      form: 'Form',
      urlCreator: 'URL Creator',
    },
    footer: {
      copyright: '© {year} Sale It To Me Baby',
    },
  },
};